/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "./Sidebar";
import api from "../api/axiosConfig";
import { useAuth } from "../context/AuthContext";
import { Bars3Icon } from "@heroicons/react/24/outline";

import CompanySwitcher from "./CompanySwitcher";
import FiscalYearSwitcher from "./FiscalYearSwitcher";
import AiReportsChatLauncher from "./AiReportsChatLauncher";
import ClientSwitchWarningModal from "./ClientSwitchWarningModal";
import { isAccountingClient } from "../utils/tenantType";
import useDocumentTitle from "../hooks/useDocumentTitle";

const getPreferredAccountingClientId = (options, storedId, tenantType) => {
  const safeOptions = Array.isArray(options) ? options : [];
  const billingClient = safeOptions.find((option) => option?.clientCode === "CL001");

  // Billing tenants should always default to CL001 when available.
  if (tenantType === "billing" && billingClient?._id) {
    return billingClient._id;
  }

  if (storedId && safeOptions.some((option) => option?._id === storedId)) {
    return storedId;
  }

  const preferredClient =
    safeOptions.find(
      (option) => option?.clientType === "billing" && option?.clientCode === "CL001"
    );

  return preferredClient?._id || safeOptions[0]?._id || "";
};

const Layout = ({ children, pageTitle }) => {
  useDocumentTitle(pageTitle);

  const [userData, setUserData] = useState({ role: null, username: "Guest" });
  const [loading, setLoading] = useState(true);
  const [tenantType, setTenantType] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // context switcher
  const [contextKind, setContextKind] = useState(null); // "store" | "accounting_client" | null
  const [contextOptions, setContextOptions] = useState([]);
  const [activeContextId, setActiveContextId] = useState("");
  const [contextLoading, setContextLoading] = useState(false);
  const [switchWarning, setSwitchWarning] = useState({
    open: false,
    targetId: null,
    clientName: '',
    activeUsers: [],
  });
  const [switchErrorToast, setSwitchErrorToast] = useState("");
  const [switchKey, setSwitchKey] = useState(0);
  const previousCompanyRef = useRef(null);

  const { getAccessToken, user, isAuthenticated, isAuthLoading, switchClient } = useAuth();

  // billing_ff is always locked to one client — never show the switcher regardless of stores length
  const isSingleClientUser = user?.role === 'billing_ff';

  const generateAvatar = (name) => {
    const safeName = name || "U";
    return (
      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold">
        {safeName.charAt(0).toUpperCase()}
      </div>
    );
  };

  const switcherLabel = useMemo(() => {
    // always label as "Company"
    return "Company";
  }, []);

  // Derive role + username from AuthContext user (no token decode needed)
  useEffect(() => {
    if (!user) return;
    setUserData({
      role: user.role || "cashier",
      username: user.username || "User",
    });
    setLoading(false);
  }, [user]);

  // Fetch tenant type for launcher visibility
  useEffect(() => {
    if (!isAuthenticated || !getAccessToken()) return;

    const fetchTenant = async () => {
      try {
        const meRes = await api.get("/me");
        const tenantData = meRes.data?.tenant || null;
        if (tenantData) {
          setTenantType(tenantData.tenantType || null);
        }
      } catch (err) {
        console.error("Failed to fetch tenant type:", err);
      }
    };
    fetchTenant();
  }, [isAuthenticated, switchKey]);

  // Bootstrap context for switcher
  useEffect(() => {
    if (!isAuthenticated || !getAccessToken()) return;

    const bootstrapContext = async () => {
      try {
        setContextLoading(true);

        const meRes = await api.get("/me");
        const me = meRes.data;

        if (!me?.contextKind) {
          // superadmin or no context
          setContextKind(null);
          setContextOptions([]);
          setActiveContextId("");
          localStorage.removeItem("contextKind");
          return;
        }

        setContextKind(me.contextKind);
        localStorage.setItem("contextKind", me.contextKind);

        const ctxRes = await api.get("/context/options");
        const ctx = ctxRes.data;

        const options = Array.isArray(ctx?.options) ? ctx.options : [];
        setContextOptions(options);

        if (ctx?.kind === "store") {
          const stored = localStorage.getItem("storeId");
          const fallback = options?.[0]?._id || "";
          const nextId = stored || fallback;
          setActiveContextId(nextId);
          if (!stored && nextId) localStorage.setItem("storeId", nextId);
          localStorage.removeItem("accountingClientId");
          localStorage.removeItem("accountingClientName");
          sessionStorage.removeItem('selectedAccountingClient');
        }

        if (ctx?.kind === "accounting_client") {
          localStorage.removeItem("storeId");

          // billing_ff: bypass the CL001 preference logic entirely — always use the
          // client assigned in the JWT stores, then lock the token via switch-client.
          if (user?.role === 'billing_ff') {
            const assignedId = Array.isArray(user?.stores) && user.stores.length > 0
              ? user.stores[0].toString()
              : options[0]?._id?.toString() || '';

            if (assignedId) {
              setActiveContextId(assignedId);
              localStorage.setItem("accountingClientId", assignedId);
              const match = options.find(o => o._id?.toString() === assignedId || o._id === assignedId);
              if (match) localStorage.setItem("accountingClientName", match.name || '');

              // Only call switch-client once per session (sessionStorage acts as the flag)
              const alreadyLocked = sessionStorage.getItem('selectedAccountingClient');
              if (!alreadyLocked) {
                switchClient(assignedId).catch(() => {});
              }
            }
            return; // skip the regular switcher flow entirely for billing_ff
          }

          const stored = localStorage.getItem("accountingClientId");
          const tenantType = me?.tenant?.tenantType || me?.tenantType || null;
          const nextId = getPreferredAccountingClientId(options, stored, tenantType);
          setActiveContextId(nextId);
          if (!stored && nextId) {
            localStorage.setItem("accountingClientId", nextId);
            const match = options.find(o => o._id === nextId);
            if (match) localStorage.setItem("accountingClientName", match.name || match.companyName || "");
          } else if (stored && nextId) {
            // Refresh name in case it changed
            const match = options.find(o => o._id === nextId);
            if (match) localStorage.setItem("accountingClientName", match.name || match.companyName || "");
            if (stored !== nextId) {
              localStorage.setItem("accountingClientId", nextId);
            }
          }

          // Tell backend which client this session started in
          if (nextId) {
            api.patch('/auth/active-client', { clientId: nextId })
              .catch(() => {}); // non-critical
          }

          // Restore selectedClient token context from sessionStorage (survives F5)
          const storedClient = sessionStorage.getItem('selectedAccountingClient');
          if (storedClient) {
            try {
              const clientObj = JSON.parse(storedClient);
              // Delegate to context switchClient — sets selectedClient + re-issues token
              switchClient(clientObj._id)
                .then(() => {
                  setActiveContextId(clientObj._id);
                  localStorage.setItem('accountingClientId', clientObj._id);
                  if (clientObj.name) localStorage.setItem('accountingClientName', clientObj.name);
                })
                .catch(() => {
                  sessionStorage.removeItem('selectedAccountingClient');
                });
            } catch {
              sessionStorage.removeItem('selectedAccountingClient');
            }
          }
        }
      } catch (err) {
        console.error("Context bootstrap error:", err);
      } finally {
        setContextLoading(false);
      }
    };

    bootstrapContext();
  }, [isAuthenticated, switchKey]);

  const handleCompanySelect = async (id) => {
    // Remove debug logs
    // Only check for accounting_client context
    if (contextKind === 'accounting_client' && id !== activeContextId) {
      try {
        const res = await api.get(
          `/auth/active-client-users?clientId=${id}`
        );
        const activeUsers = res.data?.users || [];

        if (activeUsers.length > 0) {
          // Find client name for the warning modal
          const match = contextOptions.find(o => o._id === id);
          const clientName = match?.name || 
                             match?.companyName || 
                             'this client';

          setSwitchWarning({
            open: true,
            targetId: id,
            clientName,
            activeUsers,
          });
          return; // Don't switch yet — wait for user choice
        }
      } catch {
        // If check fails, proceed normally (don't block)
      }
    }

    // No conflict or not accounting_client — proceed directly
    performSwitch(id);
  };

  const performSwitch = async (id) => {
    previousCompanyRef.current = {
      activeContextId,
      contextKind,
      accountingClientId: localStorage.getItem('accountingClientId'),
      accountingClientName: localStorage.getItem('accountingClientName'),
      storeId: localStorage.getItem('storeId'),
    };

    setActiveContextId(id);

    if (contextKind === 'store') {
      localStorage.setItem('contextKind', 'store');
      localStorage.setItem('storeId', id);
      localStorage.removeItem('accountingClientId');
      localStorage.removeItem('accountingClientName');
      sessionStorage.removeItem('selectedAccountingClient');
      localStorage.removeItem('activeFiscalYearId');
    } else if (contextKind === 'accounting_client') {
      localStorage.setItem('contextKind', 'accounting_client');
      localStorage.setItem('accountingClientId', id);
      const match = contextOptions.find(o => o._id === id);
      if (match) {
        localStorage.setItem(
          'accountingClientName', 
          match.name || match.companyName || ''
        );
      }
      localStorage.removeItem('storeId');

      // Get a fresh accessToken scoped to the new client — delegate to context
      try {
        await switchClient(id);
      } catch (err) {
        console.error('switch-client token refresh failed:', err);
        const previous = previousCompanyRef.current;

        // Restore previous context if token re-issue failed.
        if (previous) {
          setActiveContextId(previous.activeContextId || "");
          if (previous.contextKind) {
            localStorage.setItem('contextKind', previous.contextKind);
          }

          if (previous.accountingClientId) {
            localStorage.setItem('accountingClientId', previous.accountingClientId);
          } else {
            localStorage.removeItem('accountingClientId');
          }

          if (previous.accountingClientName) {
            localStorage.setItem('accountingClientName', previous.accountingClientName);
          } else {
            localStorage.removeItem('accountingClientName');
          }

          if (previous.storeId) {
            localStorage.setItem('storeId', previous.storeId);
          } else {
            localStorage.removeItem('storeId');
          }
        }

        setSwitchErrorToast('Failed to switch company. Please try again.');
        setTimeout(() => setSwitchErrorToast(''), 3000);
        window.dispatchEvent(new CustomEvent('company-context:changed', {
          detail: { contextKind: previous?.contextKind, companyId: previous?.activeContextId || null }
        }));
        return;
      }
    }

    // 🔔 Fire custom event so useCompanyContext hook in child components detects the change
    // (localStorage events don't fire in same tab, so we dispatch a custom event instead)
    window.dispatchEvent(new CustomEvent('company-context:changed', { detail: { contextKind, companyId: id } }));

    setSwitchKey(prev => prev + 1);
  };

  const handleWarningProceed = () => {
    const { targetId } = switchWarning;
    setSwitchWarning({ 
      open: false, 
      targetId: null, 
      clientName: '', 
      activeUsers: [] 
    });
    performSwitch(targetId);
  };

  const handleWarningCancel = () => {
    setSwitchWarning({ 
      open: false, 
      targetId: null, 
      clientName: '', 
      activeUsers: [] 
    });
  };

  // AccountingClientId for FY selector
  const accountingClientId = useMemo(() => {
    if (contextKind !== "accounting_client") return null;
    return localStorage.getItem("accountingClientId") || activeContextId || null;
  }, [contextKind, activeContextId]);

  if (isAuthLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex">
      {/* Mobile sidebar backdrop */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      <Sidebar
        role={userData.role}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      <div className="flex-1 bg-gray-100 min-h-screen min-w-0">
        {switchErrorToast && (
          <div className="fixed top-4 right-4 z-[100] rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 shadow">
            {switchErrorToast}
          </div>
        )}

        <header className="bg-white shadow-sm p-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            {/* Hamburger — mobile only */}
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="md:hidden p-1.5 rounded-md hover:bg-gray-100 text-gray-600"
              aria-label="Open menu"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            {/*  Company switcher — hidden for billing_ff (single-client users) */}
            {contextKind && !isSingleClientUser && (
              <CompanySwitcher
                label={switcherLabel}
                kind={contextKind}
                options={contextOptions}
                activeId={activeContextId}
                onSelect={handleCompanySelect}
                loading={contextLoading}
              />
            )}

            {/* ✅ Fiscal year switcher (accounting only) */}
            {contextKind === "accounting_client" && (
              <FiscalYearSwitcher accountingClientId={accountingClientId} />
            )}
          </div>

          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm text-gray-600">Hi, {userData.username}</p>
              <span className="text-xs px-2 py-1 bg-teal-100 text-teal-800 rounded-full">
                {(userData.role || "").toUpperCase()}
              </span>
            </div>

            {typeof userData.avatar === "string" ? (
              <img
                src={userData.avatar}
                alt={userData.username}
                className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
              />
            ) : (
              userData.avatar || generateAvatar(userData.username)
            )}
          </div>
        </header>

        <main className="p-4">{children}</main>

        <footer className="px-4 py-3 text-xs text-gray-500 flex justify-center gap-4">
          <a href="/terms" target="_blank" rel="noreferrer" className="hover:text-gray-700 underline">
            Terms
          </a>
          <a href="/privacy" target="_blank" rel="noreferrer" className="hover:text-gray-700 underline">
            Privacy
          </a>
        </footer>
      </div>

      {contextKind === "accounting_client" && isAccountingClient(tenantType) && (
        <AiReportsChatLauncher
          accountingClientId={accountingClientId}
          username={userData.username}
        />
      )}

      <ClientSwitchWarningModal
        isOpen={switchWarning.open}
        clientName={switchWarning.clientName}
        activeUsers={switchWarning.activeUsers}
        onProceed={handleWarningProceed}
        onCancel={handleWarningCancel}
      />
    </div>
  );
};

export default Layout;
